/**
 * Dropdown Field UI component
 * @Author: Junaid Atari junaid.attari@invozone.dev
 * @Date: 2025-02-17 17:24:24
 */

import React, {useCallback, useEffect, useState} from 'react';

// ui
import {Listbox, Combobox, type ComboboxProps, type ListboxProps} from '@shopify/polaris';

// types
import type {FormikProps, FormikValues} from 'formik';
import type {FieldInfo, ListItem} from '~/types/field.types';

export interface DropdownFieldProps {
  /** The field info schema */
  fieldInfo: FieldInfo;

  /** The formik instance */
  formik: FormikProps<any>;

  /** Polaris combobox component properties  */
  comboboxProps?: Partial<ComboboxProps>;

  /** Polaris listbox component properties  */
  listboxProps?: Partial<ListboxProps>;

  /** Listbox options to display when dropdown is opened */
  items?: ListItem[];

  /**
   * Callback fired when the value is changed
   * @param value - The updated value
   * @param fieldInfo t- Te field info
   * @param formik - The formik instance
   */
  afterChange?(
    value: unknown,
    fieldInfo: FieldInfo,
    formik: FormikProps<any>,
  ): void;
}

const DropdownField: React.FC<DropdownFieldProps> = (props) => {
  const items = Array.isArray(props.items) ? props.items : props.fieldInfo.items ?? [];


  const isTouched = props.formik.touched[props.fieldInfo.key as keyof FormikValues];

  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState(items);

  const escapeSpecialRegExCharacters = useCallback(
    (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    [],
  );

  const updateText = useCallback(
    (value: string) => {
      setInputValue(value);

      if (value === '') {
        setOptions(items);
        return;
      }

      const filterRegex = new RegExp(escapeSpecialRegExCharacters(value), 'i');
      const resultOptions = items.filter((option) =>
        option.label.match(filterRegex),
      );
      setOptions(resultOptions);
    },
    [items, escapeSpecialRegExCharacters],
  );

  const updateSelection = useCallback(
    (selected: string) => {
      const matchedOption = options.find((option) => {
        return option.value.match(selected);
      });

      props.formik.setFieldValue(props.fieldInfo.key, matchedOption?.value, true);
      props.afterChange?.(matchedOption?.value, props.fieldInfo, props.formik);
      setInputValue((matchedOption && matchedOption.label) || '');
      props.formik.setFieldError(props.fieldInfo.key, '');
      props.formik.setFieldTouched(props.fieldInfo.key, false, false);
    },
    [options],
  );

  useEffect(() => {
    const selectedItem = items?.find((option) => {
      return option.value === props.formik.values[props.fieldInfo.key];
    });
    if (selectedItem)
      updateSelection(selectedItem?.value ?? '')
  }, []);

  return (
    <>
      <Combobox
        {...(props.comboboxProps ?? {})}
        activator={
          <Combobox.TextField
            prefix={false}
            requiredIndicator={props.fieldInfo.required}
            label={props.fieldInfo.label}
            value={inputValue}
            placeholder={props.fieldInfo.placeholder}
            autoComplete="off"
            onChange={updateText}
            error={isTouched ? String(props.formik.errors?.[props.fieldInfo.key] ?? '') : false}
            clearButton
            onClearButtonClick={() => {
              setInputValue('');
              setOptions(items);
              props.formik.setFieldValue(props.fieldInfo.key, '', true);
              props.afterChange?.(null, props.fieldInfo, props.formik);
            }}
          />
        }>
        <Listbox
          {...(props.listboxProps ?? {})}
          onSelect={updateSelection}>
          {options.map(({label, value}) => {
            return (
              <Listbox.Option
                key={`${value}`}
                value={value}
                selected={value === props.formik.values[props.fieldInfo.key]}
                accessibilityLabel={label}>
                {label}
              </Listbox.Option>
            );
          })}
          {
            options.length === 0 && (
              <Listbox.Option
                key="no-results"
                value="no-results"
                selected={false}
                accessibilityLabel="No results">
                No results
              </Listbox.Option>
            )
          }
        </Listbox>
      </Combobox>
      {props.fieldInfo?.helpText && (
        <div className="help-text">{props.fieldInfo.helpText}</div>
      )}
    </>
  );
};

export default DropdownField;
